<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { CHAT_PDF_MEDIA_TYPE, chatRoleEnum, useChatStore } from '@/stores/chat.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { formatRecentDateTime } from '@/composables/date-time'
import { useTypewriter } from '@/composables/typewriter'
import { pxToRem } from '@/lib/utils'

import TypewriterText from '@/components/shared/TypewriterText.vue'
import DockablePanelHeader from '@/components/shared/dockable-panel/DockablePanelHeader.vue'
import ChatDraftCard from '@/components/chat/ChatDraftCard.vue'
import ChatMessageText from '@/components/chat/ChatMessageText.vue'
import ChatHistory from '@/components/chat/ChatHistory.vue'
import ChatMessageActions from '@/components/chat/ChatMessageActions.vue'
import ChatActionCard from '@/components/chat/ChatActionCard.vue'
import ChatChecklistCard from '@/components/chat/ChatChecklistCard.vue'
import ChatQuickReplies from '@/components/chat/ChatQuickReplies.vue'
import ImagePreviewDialog from '@/components/shared/ImagePreviewDialog.vue'
import ChatPhotoThumb from '@/components/chat/ChatPhotoThumb.vue'
import ChatActionButtons from '@/components/chat/ChatActionButtons.vue'

defineProps({
  floating: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['move', 'open-draft'])

const i18n = getI18n()
const chatStore = useChatStore()
const webSiteStore = useWebSiteStore()

const hints = computed(() => chatStore.hintKeys.map(key => i18n.t(key)))
const scrollArea = ref(null)

const TOKENS_PER_K = 1000
const CHAT_ATTACHMENT_TYPES = `image/*,${CHAT_PDF_MEDIA_TYPE}`
const tokenFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 })

const headerCaption = computed(() => {
  const streamingStage = chatStore.messages.at(-1)?.stage
  if (streamingStage !== null && streamingStage !== undefined) return i18n.t(chatStore.getStageLabel(streamingStage))
  if (chatStore.showHistory) return i18n.t('chat.conversationCount', { count: chatStore.conversations.length })
  if (!chatStore.currentConversation) return i18n.t('chat.tagline')
  return i18n.t('chat.conversationCaption', {
    tokens: tokenFormat.format(chatStore.conversationTokens / TOKENS_PER_K),
    time: formatRecentDateTime(chatStore.currentConversation.updated_at),
  })
})

const typedTitle = useTypewriter(() => chatStore.currentConversation?.title ?? null, () => chatStore.conversationId)

const headerTitle = computed(() => {
  if (chatStore.showHistory) return i18n.t('chat.history')
  return typedTitle.value ?? i18n.t('chat.title')
})

const isSent = (message) => message.role === chatRoleEnum.USER

const BOTTOM_TOLERANCE_PX = 48
const stickToBottom = ref(true)

const scrollToBottom = () => {
  stickToBottom.value = true
  nextTick(() => scrollArea.value?.setScrollPercentage('vertical', 1))
}

let lastScrollPosition = 0

// The scroll event also fires when the content grows: only an upward move is the user leaving the bottom.
const onScroll = ({ verticalPosition, verticalSize, verticalContainerSize }) => {
  if (verticalSize - verticalContainerSize - verticalPosition <= BOTTOM_TOLERANCE_PX) {
    stickToBottom.value = true
  } else if (verticalPosition < lastScrollPosition) {
    stickToBottom.value = false
  }
  lastScrollPosition = verticalPosition
}

const onContentResize = () => {
  if (stickToBottom.value) scrollArea.value?.setScrollPercentage('vertical', 1)
}

watch(() => chatStore.conversationId, scrollToBottom)
watch(() => chatStore.showHistory, (showHistory) => { if (!showHistory) scrollToBottom() })

onMounted(async() => {
  await chatStore.restoreConversationAsync()
  scrollToBottom()
})

const sendAsync = async(content) => {
  if ((!content && !chatStore.pendingPhoto) || chatStore.isStreaming) return
  scrollToBottom()
  const answer = await chatStore.sendMessageAsync(content)
  if (answer.draft && !answer.error) emit('open-draft', answer)
}

const outgoingMessage = computed(() => chatStore.prompt.trim())

const photoInput = ref(null)
const openedPhoto = ref(null)

const onPhotoPicked = async(event) => {
  const [file] = event.target.files
  event.target.value = ''
  if (!file) return
  if (editing.value) editing.value.photo = await chatStore.toPhotoAsync(file)
  else await chatStore.attachPhotoAsync(file)
}

const onSend = async() => {
  const content = outgoingMessage.value
  chatStore.prompt = ''
  await sendAsync(content)
}

const editing = ref(null)
const messageContentElements = new Map()

const setMessageContentElement = (key, el) => {
  if (el) messageContentElements.set(key, el)
  else messageContentElements.delete(key)
}

const onEditStart = (message) => {
  const { width, height } = messageContentElements.get(message.key).getBoundingClientRect()
  editing.value = { message, content: message.content, photo: message.files[0] ?? null, width: pxToRem(width), height: pxToRem(height) }
}

const onEditConfirm = async() => {
  const { message, content, photo } = editing.value
  if (!content.trim()) return
  editing.value = null
  await chatStore.editMessageAsync(message, content.trim(), photo)
}

const isLastMessage = (message) => message === chatStore.messages.at(-1)

</script>

<template>
  <div class="column no-wrap full-height relative-position">
    <dockable-panel-header
      v-model:docked="webSiteStore.chatPanelDocked"
      icon="mdi-robot-happy-outline"
      :title="headerTitle"
      :caption="headerCaption"
      :floating="floating"
      @move="emit('move', $event)"
      @close="chatStore.showChat = false"
    >
      <template #actions>
        <q-btn
          flat
          round
          dense
          icon="mdi-history"
          :color="chatStore.showHistory ? 'primary' : undefined"
          @click="chatStore.showHistory = !chatStore.showHistory"
        >
          <q-tooltip>{{ i18n.t('chat.history') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          dense
          icon="mdi-message-plus-outline"
          :disable="!chatStore.messages.length"
          @click="chatStore.newConversation()"
        >
          <q-tooltip>{{ i18n.t('chat.newConversation') }}</q-tooltip>
        </q-btn>
      </template>
    </dockable-panel-header>

    <chat-history v-if="chatStore.showHistory" class="col" />
    <template v-else>
      <transition
        appear
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
      >
        <div v-if="!chatStore.messages.length" class="col column flex-center q-pa-lg text-center">
          <q-icon
            name="mdi-creation"
            color="primary"
            size="xl"
            class="q-mb-md"
          />
          <div class="chat-hints text-subtitle1 text-grey-8">
            <typewriter-text :phrases="hints" :pause="4000" />
          </div>
        </div>
      </transition>
      <q-scroll-area
        v-show="chatStore.messages.length"
        ref="scrollArea"
        class="col chat-messages"
        @scroll="onScroll"
      >
        <div class="relative-position q-px-md q-pb-md">
          <q-resize-observer @resize="onContentResize" />
          <div
            v-for="message in chatStore.messages"
            :key="message.key"
            class="chat-message"
            :class="isSent(message) ? 'chat-message--sent' : 'chat-message--received'"
          >
            <q-chat-message
              :sent="isSent(message)"
              :name="isSent(message) ? i18n.t('chat.you') : i18n.t('chat.title')"
            >
              <template v-if="!isSent(message)" #name>
                <span class="row inline items-center no-wrap q-gutter-x-xs">
                  <q-icon name="mdi-robot-happy-outline" size="xs" />
                  <span>{{ i18n.t('chat.title') }}</span>
                </span>
              </template>
              <div
                v-if="editing?.message === message"
                class="chat-message-edit"
                :style="{ width: `${editing.width}rem`, minHeight: `${editing.height}rem` }"
              >
                <chat-photo-thumb
                  v-if="editing.photo"
                  class="q-mb-xs"
                  :photo="editing.photo"
                  removable
                  @open="openedPhoto = $event"
                  @remove="editing.photo = null"
                />
                <q-input
                  v-model="editing.content"
                  dense
                  :outlined="false"
                  borderless
                  autogrow
                  autofocus
                  @keydown.enter.exact.prevent="onEditConfirm"
                  @keydown.esc="editing = null"
                />
              </div>
              <div v-else-if="message.content" :ref="el => setMessageContentElement(message.key, el)">
                <div v-if="message.files.length" class="row q-gutter-xs q-mb-xs">
                  <chat-photo-thumb
                    v-for="file in message.files"
                    :key="file.id ?? file.name"
                    :photo="file"
                    @open="openedPhoto = $event"
                  />
                </div>
                <chat-message-text
                  :text="message.content"
                  :form-links="message.form_links"
                  :has-draft="!!message.draft"
                />
                <chat-quick-replies
                  v-if="message.quick_replies && isLastMessage(message)"
                  :quick-replies="message.quick_replies"
                  :disable="chatStore.isStreaming"
                />
              </div>
              <div v-if="message.stage !== null" class="row items-center no-wrap q-gutter-x-sm text-grey-8">
                <q-spinner-audio color="primary" size="0.9375rem" />
                <span>{{ i18n.t(chatStore.getStageLabel(message.stage)) }}</span>
              </div>
              <div v-if="message.error" class="text-negative">{{ i18n.t(message.error) }}</div>
              <chat-draft-card v-if="message.draft" :message="message" @open="emit('open-draft', $event)" />
              <div v-if="message.task_ids?.length || message.actions.length">
                <chat-checklist-card v-if="message.task_ids?.length" :task-ids="message.task_ids" />
                <chat-action-card
                  v-for="action in message.actions"
                  :key="action.tool_call_id"
                  :message="message"
                  :action="action"
                />
                <chat-action-buttons v-if="chatStore.pendingActions(message).length > 1" :message="message" />
              </div>
            </q-chat-message>
            <div v-if="editing?.message === message" class="row items-center justify-end no-wrap q-gutter-x-xs q-mt-xs">
              <q-btn
                flat
                round
                dense
                color="grey-7"
                icon="mdi-camera-plus-outline"
                @click="photoInput.click()"
              >
                <q-tooltip>{{ i18n.t('chat.attachFile') }}</q-tooltip>
              </q-btn>
              <q-btn
                flat
                dense
                no-caps
                :label="i18n.t('common.cancel')"
                @click="editing = null"
              />
              <q-btn
                unelevated
                dense
                no-caps
                color="primary"
                :label="i18n.t('chat.send')"
                :disable="!editing.content.trim()"
                @click="onEditConfirm"
              />
            </div>
            <chat-message-actions
              v-if="message.content && message.stage === null && editing?.message !== message"
              :message="message"
              @edit="onEditStart"
            />
          </div>
        </div>
      </q-scroll-area>
      <transition
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
      >
        <q-btn
          v-if="chatStore.messages.length && !stickToBottom"
          round
          unelevated
          color="primary"
          icon="mdi-arrow-down"
          class="chat-scroll-bottom"
          @click="scrollToBottom"
        >
          <q-tooltip>{{ i18n.t('chat.scrollToBottom') }}</q-tooltip>
        </q-btn>
      </transition>
      <div v-if="!chatStore.messages.length" class="chat-quick-actions row q-px-sm q-pt-xs">
        <q-chip
          v-for="quickAction in chatStore.quickActions"
          :key="quickAction"
          dense
          clickable
          outline
          color="primary"
          icon="mdi-lightning-bolt-outline"
          @click="sendAsync(i18n.t(quickAction))"
        >
          <span class="ellipsis">{{ i18n.t(quickAction) }}</span>
          <q-tooltip>{{ i18n.t(quickAction) }}</q-tooltip>
        </q-chip>
      </div>
    </template>
    <div v-show="!chatStore.showHistory" class="q-pa-sm">
      <div class="row items-end no-wrap q-gutter-x-sm">
        <input
          ref="photoInput"
          type="file"
          :accept="CHAT_ATTACHMENT_TYPES"
          class="hidden"
          @change="onPhotoPicked"
        >
        <q-btn
          round
          flat
          color="grey-7"
          icon="mdi-camera-plus-outline"
          :disable="chatStore.isStreaming || editing !== null"
          @click="photoInput.click()"
        >
          <q-tooltip>{{ i18n.t('chat.attachFile') }}</q-tooltip>
        </q-btn>
        <div class="col chat-composer" :class="{ 'chat-composer--disabled': editing !== null }">
          <div v-if="chatStore.pendingPhoto" class="row items-center chat-composer__attachments">
            <chat-photo-thumb
              :photo="chatStore.pendingPhoto"
              removable
              @open="openedPhoto = $event"
              @remove="chatStore.removePhoto()"
            />
          </div>
          <q-input
            v-model="chatStore.prompt"
            class="chat-composer__input"
            :disable="editing !== null"
            :outlined="false"
            borderless
            dense
            autogrow
            :placeholder="i18n.t(chatStore.pendingPhoto ? 'chat.attachmentPlaceholder' : 'chat.placeholder')"
            @keydown.enter.exact.prevent="onSend"
          />
        </div>
        <q-btn
          v-if="chatStore.isStreaming"
          round
          flat
          color="negative"
          icon="mdi-stop"
          @click="chatStore.stopStreamingAsync()"
        >
          <q-tooltip>{{ i18n.t('chat.stop') }}</q-tooltip>
        </q-btn>
        <q-btn
          v-else
          round
          flat
          color="primary"
          icon="mdi-send"
          :disable="editing !== null || (!outgoingMessage && !chatStore.pendingPhoto)"
          @click="onSend"
        >
          <q-tooltip>{{ i18n.t('chat.send') }}</q-tooltip>
        </q-btn>
      </div>
    </div>
    <image-preview-dialog :image="openedPhoto && { name: openedPhoto.name, src: openedPhoto.preview }" @close="openedPhoto = null" />
  </div>
</template>

<style scoped lang="scss">
.chat-messages :deep(.q-scrollarea__content) {
  width: 100%;
}

@media (hover: hover) {
  .chat-message :deep(.chat-message-actions--sent) {
    opacity: 0;
  }
  .chat-message:hover :deep(.chat-message-actions--sent),
  .chat-message:focus-within :deep(.chat-message-actions--sent) {
    opacity: 1;
  }
}

.chat-composer {
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: $generic-border-radius;
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }
  &:focus-within {
    border-color: $primary;
    box-shadow: 0 0 0 0.25rem rgba($primary, 0.15);
  }
}

.chat-composer--disabled {
  opacity: .6;
  pointer-events: none;
}

.chat-composer__attachments {
  gap: .5rem;
  min-height: 6rem;
  padding: 1.125rem .75rem .5rem;
  border-bottom: 1px solid $grey-3;
}

.chat-composer__input {
  padding: 0 .75rem;
}

.chat-message {
  min-width: 0;
  :deep(.q-message),
  :deep(.q-message-container > div),
  :deep(.q-message-text),
  :deep(.q-message-text-content) {
    min-width: 0;
    max-width: 100%;
  }
  :deep(.q-message-name) {
    font-size: .75rem;
    font-weight: 600;
    color: $grey-7;
    margin-bottom: .25rem;
  }
  :deep(.q-message-text) {
    min-height: 0;
    padding: .625rem .875rem;
    border-radius: $app-border-radius;
    font-size: .875rem;
    line-height: 1.6;
    color: $grey-9;
    &::before {
      display: none;
    }
  }
  :deep(.q-message-text-content) {
    color: $grey-9;
  }
}

.chat-message--sent {
  margin-left: 2.5rem;
  :deep(.q-message-text) {
    background: rgba($primary, 0.1);
    border: 1px solid rgba($primary, 0.35);
    border-bottom-right-radius: .25rem;
  }
}

.chat-message--received {
  margin-right: 2.5rem;

  :deep(.q-message-text) {
    background: $white;
    border: 1px solid $grey-3;
    border-bottom-left-radius: .25rem;
    box-shadow: 0 .125rem .5rem rgba(0, 0, 0, 0.04);
  }
}

.chat-message-edit {
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  :deep(.q-field__control),
  :deep(.q-field__native) {
    min-height: 0;
    height: auto;
    padding: 0;
    font: inherit;
    line-height: inherit;
    color: inherit;
  }
}

.chat-scroll-bottom {
  position: absolute;
  left: 50%;
  bottom: 5rem;
  transform: translateX(-50%);
  z-index: 2;
  box-shadow: 0 .25rem .75rem rgba(0, 0, 0, 0.18);
}

.chat-hints {
  min-height: 4.5em;
  max-width: 24rem;
}

.chat-quick-actions {
  flex: none;
  min-width: 0;
  .q-chip {
    max-width: 100%;
  }
}

</style>
